import {
  collection,
  onSnapshot,
  query,
  where,
  addDoc,
  orderBy,
  updateDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import moment from "moment";
import { createContext, useContext, useEffect, useState } from "react";
import { IBusiness } from "../interfaces/IBusiness";
import { packageStatusPhrase } from "../utils/functions";
import { BusinessContext } from "./BusinessContext";
import { FirebaseContext } from "./FirebaseContext";

export interface IParcelProps {
  tickets: any;
  ticketsClients: any;
  selectedTicketsUser: { pending: IDoc[]; closed: IDoc[] } | null;
  selectedTicketsBusiness: { pending: IDocB[]; closed: IDocB[] } | null;
  selectedTicketsUserClient: { pending: IDoc[]; closed: IDoc[] } | null;
  selectedTicketsBusinessClient: { pending: IDocB[]; closed: IDocB[] } | null;
  allTickets: {
    id: any;
    tickets: {
      pending: IDoc[];
      closed: IDoc[];
    };
    businessData: IBusiness;
  }[];
  loading: boolean;
  packagesToDownload: {
    recibido: string;
    entregado: string;
    comentarios: string;
    usuario: string;
    estado: string;
  }[];
  packagesToDownloadClient: {
    recibido: string;
    entregado: string;
    comentarios: string;
    usuario: string;
    estado: string;
  }[];
  addTicket: (data: IDoc) => Promise<void>;
  addTicketClient: (data: IDoc) => Promise<void>;
  addTicketBusiness: (data: IDocB) => Promise<void>;
  addTicketBusinessClient: (data: IDocB) => Promise<void>;
  updateTicketStatus: (status: string, id: string) => Promise<void>;
  updateTicketStatusClient: (status: string, id: string) => Promise<void>;
  responsableTickets: {
    closed: IDocB[];
    pending: IDocB[];
  } | null;
  getTicketsByBusinessId: (id: string) => Promise<void>;
  getTicketsByUserId: (id: string) => Promise<void>;
  getTicketsByBusinessIdClient: (id: string) => Promise<void>;
  getTicketsByUserIdClient: (id: string) => Promise<void>;
  loadPackagesForAdmin: () => void;
  loadPackagesForAdminClients: () => void;
}

export type IDoc = {
  id?: string;
  reason: string;
  userId: string;
  businessId: string;
  // Nombre del destinatario al crear el paquete (respaldo si luego se elimina el usuario)
  recipientName?: string;
  comments: string;
  timestamp: number;
  type: "user";
  status: "pending" | "closed";
  lastUpdate?: number;
};

export type IDocB = {
  id?: string;
  reason: string;
  businessId: string;
  // Nombre de la empresa al crear el paquete (respaldo si luego se elimina la empresa)
  recipientName?: string;
  comments: string;
  timestamp: number;
  type: "business";
  status: "pending" | "closed";
  lastUpdate?: number;
};

export const ParcelContext = createContext<IParcelProps>(null);

export const ParcelProvider: any = ({ children }: any) => {
  const [tickets, setTickets] = useState(null);
  const [ticketsClients, setTicketsClients] = useState(null);

  const [selectedTicketsUser, setSelectedTicketsUser] = useState<{ pending: IDoc[]; closed: IDoc[] } | null>(null);
  const [selectedTicketsBusiness, setSelectedTicketsBusiness] = useState<{ pending: IDocB[]; closed: IDocB[] } | null>(null);
  const [selectedTicketsUserClient, setSelectedTicketsUserClient] = useState<{ pending: IDoc[]; closed: IDoc[] } | null>(null);
  const [selectedTicketsBusinessClient, setSelectedTicketsBusinessClient] = useState<{ pending: IDocB[]; closed: IDocB[] } | null>(null);

  const [packagesToDownload, setPackagesToDownload] = useState<any[]>([]);
  const [packagesToDownloadClient, setPackagesToDownloadClient] = useState<any[]>([]);
  const [allTickets, setAllTickets] = useState([]);
  const { getUserData } = useContext(FirebaseContext);
  const { getBusinessById } = useContext(BusinessContext);

  const [responsableTickets, setResponsableTickets] = useState<{ closed: IDocB[]; pending: IDocB[] } | null>(null);
  const [loading, setLoading] = useState(true);

  const { db, user } = useContext(FirebaseContext);

  /** ================= USER ================= */
  const getTicketsByUserId = async (id: string) => {
    if (id) {
      const q = query(collection(db, "packages"), where("userId", "==", id), orderBy("timestamp", "desc"));
      onSnapshot(q, (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() } as IDoc));
        setSelectedTicketsUser({
          pending: arr.filter((d) => d.status === "pending"),
          closed: arr.filter((d) => d.status === "closed"),
        });
      });
    }
  };

  const getTicketsByUserIdClient = async (id: string) => {
    if (id) {
      const q = query(collection(db, "client-packages"), where("userId", "==", id), orderBy("timestamp", "desc"));
      onSnapshot(q, (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() } as IDoc));
        setSelectedTicketsUserClient({
          pending: arr.filter((d) => d.status === "pending"),
          closed: arr.filter((d) => d.status === "closed"),
        });
      });
    }
  };

  /** ================= BUSINESS ================= */
  const getTicketsByBusinessId = async (id: string) => {
    if (id) {
      const q = query(collection(db, "packages"), where("businessId", "==", id), where("type", "==", "business"), orderBy("timestamp", "desc"));
      onSnapshot(q, (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() } as IDocB));
        setSelectedTicketsBusiness({
          pending: arr.filter((d) => d.status === "pending"),
          closed: arr.filter((d) => d.status === "closed"),
        });
      });
    }
  };

  const getTicketsByBusinessIdClient = async (id: string) => {
    if (id) {
      const q = query(collection(db, "client-packages"), where("businessId", "==", id), where("type", "==", "business"), orderBy("timestamp", "desc"));
      onSnapshot(q, (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() } as IDocB));
        setSelectedTicketsBusinessClient({
          pending: arr.filter((d) => d.status === "pending"),
          closed: arr.filter((d) => d.status === "closed"),
        });
      });
    }
  };

  /** ================= ADD / UPDATE ================= */
  const addTicket = async (data: IDoc) => {
    await addDoc(collection(db, "packages"), { ...data });
  };

  const addTicketClient = async (data: IDoc) => {
    await addDoc(collection(db, "client-packages"), { ...data });
  };

  const addTicketBusiness = async (data: IDocB) => {
    await addDoc(collection(db, "packages"), { ...data });
  };

  const addTicketBusinessClient = async (data: IDocB) => {
    await addDoc(collection(db, "client-packages"), { ...data });
  };

  const updateTicketStatus = async (status: string, id: string) => {
    await updateDoc(doc(db, "packages", id), { status, lastUpdate: Date.now() });
  };

  const updateTicketStatusClient = async (status: string, id: string) => {
    await updateDoc(doc(db, "client-packages", id), { status, lastUpdate: Date.now() });
  };

  /** ================= HELPERS ================= */
  const getBusinessData = async (id: string) => {
    const snap = await getDoc(doc(db, "business", id));
    return snap.exists() ? (snap.data() as IBusiness) : null;
  };

  const returnFormatedDocToDowload = async (doc: IDoc | IDocB) => {
    try {
      if ((doc as IDoc).userId) {
        // tipo user
        const userDoc = await getUserData((doc as IDoc).userId);
        return {
          recibido: moment(doc.timestamp).format("DD/MM/YYYY HH:mm"),
          entregado: doc.status === "closed" ? moment(doc.lastUpdate).format("DD/MM/YYYY HH:mm") : "",
          comentarios: doc.comments,
          usuario: userDoc?.name || doc.recipientName || "Usuario no encontrado",
          estado: packageStatusPhrase(doc.status),
        };
      } else {
        // tipo business
        const businessDoc = await getBusinessById(doc.businessId);
        return {
          recibido: moment(doc.timestamp).format("DD/MM/YYYY HH:mm"),
          entregado: doc.status === "closed" ? moment(doc.lastUpdate).format("DD/MM/YYYY HH:mm") : "",
          comentarios: doc.comments,
          usuario: businessDoc?.name || doc.recipientName || "Empresa no encontrada",
          estado: packageStatusPhrase(doc.status),
        };
      }
    } catch (error) {
      console.error("Error formatting package for download:", error);
      // Devolver datos básicos si hay error
      return {
        recibido: moment(doc.timestamp).format("DD/MM/YYYY HH:mm"),
        entregado: doc.status === "closed" ? moment(doc.lastUpdate).format("DD/MM/YYYY HH:mm") : "",
        comentarios: doc.comments,
        usuario: doc.recipientName || "Error al cargar datos",
        estado: packageStatusPhrase(doc.status),
      };
    }
  };

  /** ================= DOWNLOAD LISTENERS (restaurado) ================= */
  const getAllTicketsForDownload = async () => {
    const q = query(collection(db, "packages"), orderBy("timestamp", "desc"));
    onSnapshot(q, async (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() } as IDoc | IDocB));
      const closedToday: any[] = [];
      const pendingPackages: any[] = [];

      // 🔹 Procesar documentos con manejo de errores individual
      const results = await Promise.allSettled(
        arr.map(async (doc) => {
          const todayInit = new Date().setHours(0, 0, 0, 0);
          const todayFinish = new Date().setHours(23, 59, 0, 0);
          const formattedDoc = await returnFormatedDocToDowload(doc);
          
          if (doc.status === "closed") {
            if ((doc as any).lastUpdate >= todayInit && (doc as any).lastUpdate <= todayFinish) {
              return { type: 'closed', data: formattedDoc };
            }
          } else {
            return { type: 'pending', data: formattedDoc };
          }
          return null;
        })
      );

      // 🔹 Filtrar resultados exitosos y separarlos
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          if (result.value.type === 'closed') {
            closedToday.push(result.value.data);
          } else if (result.value.type === 'pending') {
            pendingPackages.push(result.value.data);
          }
        } else if (result.status === 'rejected') {
          console.error('Error processing package:', result.reason);
        }
      });

      setPackagesToDownload(pendingPackages.concat(closedToday));
    });
  };

  const getAllTicketsForDownloadClient = async () => {
    const q = query(collection(db, "client-packages"), orderBy("timestamp", "desc"));
    onSnapshot(q, async (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() } as IDoc | IDocB));
      const closedToday: any[] = [];
      const pendingPackages: any[] = [];

      // 🔹 Procesar documentos con manejo de errores individual
      const results = await Promise.allSettled(
        arr.map(async (doc) => {
          const todayInit = new Date().setHours(0, 0, 0, 0);
          const todayFinish = new Date().setHours(23, 59, 0, 0);
          const formattedDoc = await returnFormatedDocToDowload(doc);
          
          if (doc.status === "closed") {
            if ((doc as any).lastUpdate >= todayInit && (doc as any).lastUpdate <= todayFinish) {
              return { type: 'closed', data: formattedDoc };
            }
          } else {
            return { type: 'pending', data: formattedDoc };
          }
          return null;
        })
      );

      // 🔹 Filtrar resultados exitosos y separarlos
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          if (result.value.type === 'closed') {
            closedToday.push(result.value.data);
          } else if (result.value.type === 'pending') {
            pendingPackages.push(result.value.data);
          }
        } else if (result.status === 'rejected') {
          console.error('Error processing client package:', result.reason);
        }
      });

      setPackagesToDownloadClient(pendingPackages.concat(closedToday));
    });
  };

  /** ================= INIT ================= */
  // Ya NO cargamos automáticamente, solo cuando se llame explícitamente
  const loadPackagesForAdmin = () => {
    if (user?.roles?.parcel || user?.roles?.super) {
      getAllTicketsForDownload();
    }
  };

  const loadPackagesForAdminClients = () => {
    if (user?.roles?.parcel || user?.roles?.super) {
      getAllTicketsForDownloadClient();
    }
  };

  return (
    <ParcelContext.Provider
      value={{
        tickets,
        ticketsClients,
        selectedTicketsUser,
        selectedTicketsBusiness,
        selectedTicketsUserClient,
        selectedTicketsBusinessClient,
        allTickets,
        loading,
        packagesToDownload,
        packagesToDownloadClient,
        responsableTickets,
        addTicket,
        addTicketClient,
        addTicketBusiness,
        addTicketBusinessClient,
        updateTicketStatus,
        updateTicketStatusClient,
        getTicketsByBusinessId,
        getTicketsByUserId,
        getTicketsByBusinessIdClient,
        getTicketsByUserIdClient,
        loadPackagesForAdmin,
        loadPackagesForAdminClients,
      }}
    >
      {children}
    </ParcelContext.Provider>
  );
};
